import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { withNamespaces } from "react-i18next";
import { Label } from "reactstrap";
import MultiSelect from "../../../../../../../components/MultiSelect";
import UserUtils from "src/services/utils/UserUtils";
import ReportIncidenService from "../../../../components/api/ReportIncidenService";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import CommonService from "src/modules/data-protection/apis/CommonService";

const AffectedLegals = (props) => {
    const { t, report, caseId, currentUser, reportLoading, Organization } = props;

    const [affectedLegals, setAffectedLegals] = useState([]);
    const [legalOptions, setLegalOptions] = useState([]);

    const userUtils = new UserUtils();
    const isAnalystOrAnalystAdmin = () => {
        return userUtils.isAnalystOrAnalystAdmin(currentUser);
    };

    const handleRemoveLegalMutation = useMutation({
        mutationFn: async (payload) => {
            const service = ReportIncidenService.getInstance();

            return await service.removeLegal(payload);
        },
    });

    const handleAddLegalMutation = useMutation({
        mutationFn: async (payload) => {
            const service = ReportIncidenService.getInstance();

            return await service.addLegal(payload);
        },
    });

    const handleFetchLegalEntities = useQuery({
        queryKey: ["gdpr-fetch-legal-entities-list"],
        queryFn: async () => {
            const service = CommonService.getInstance();
            return await service.legalEntities(Organization?.id);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("An error occurred while fetching legal entities."), {
                type: "error",
            });
        },
    });

    const handleRemoveLegalChange = (item) => {
        handleRemoveLegalMutation.mutate(
            {
                reportCaseId: caseId,
                legalEntityId: item[0].id,
            },
            {
                onSuccess: () => {
                    toast(t("The list of affected legal entities has been updated successfully."), {
                        type: "success",
                    });
                },
                onError: () => {
                    toast(t("Failed to update affected legal entities."), {
                        type: "error",
                    });
                    setAffectedLegals([...affectedLegals]);
                },
            }
        );
    };

    const handleAddLegalChange = (item) => {
        handleAddLegalMutation.mutate(
            {
                reportCaseId: caseId,
                legalEntityId: item[0].id,
            },
            {
                onSuccess: () => {
                    toast(t("The list of affected legal entities has been updated successfully."), {
                        type: "success",
                    });
                },
                onError: () => {
                    toast(t("Failed to update affected legal entities."), {
                        type: "error",
                    });
                    setAffectedLegals([...affectedLegals]);
                },
            }
        );
    };

    const legalEntitiesLoading =
        handleFetchLegalEntities.isFetching || handleFetchLegalEntities.isLoading;

    const determineChangesAndSendToBackend = (
        currentValues,
        previousValues,
        removeCallback,
        addCallback
    ) => {
        const removedItems = previousValues.filter(
            (item) => !currentValues.find((val) => val.id === item.id)
        );
        const addedItems = currentValues.filter(
            (val) => !previousValues.find((item) => item.id === val.id)
        );

        if (removedItems.length > 0 && addedItems.length === 0) {
            removeCallback(removedItems);
        } else if (addedItems.length > 0 && removedItems.length === 0) {
            addCallback(addedItems);
        } else {
            return;
        }
    };

    const handleMultiSelectChangeAssets = (selectedValues) => {
        determineChangesAndSendToBackend(
            selectedValues,
            affectedLegals,
            (removedItems) => {
                handleRemoveLegalChange(removedItems);
            },
            (addedItems) => {

                handleAddLegalChange(addedItems);
            }
        );
        setAffectedLegals(selectedValues);
    };

    useEffect(() => {
        if (
            !reportLoading &&
            report &&
            handleFetchLegalEntities.data?.length > 0
        ) {
            setLegalOptions(handleFetchLegalEntities.data);
            const legalObjects = handleFetchLegalEntities.data
                .filter((legal) =>
                    report?.legal_entities.some(
                        (affectedLegal) => affectedLegal.id === legal.id
                    )
                )
                .map((legal) => ({ ...legal, title: legal.title }));
            setAffectedLegals(legalObjects);
        }
    }, [handleFetchLegalEntities.data, report, reportLoading]);

    return (
        <div>
            <Label>{t("Affected Legal Entities")}</Label>
            {!legalEntitiesLoading && !reportLoading ? (
                <MultiSelect
                    options={
                        legalOptions && legalOptions?.length > 0
                            ? legalOptions : []
                    }
                    selectedValues={affectedLegals}
                    onChange={handleMultiSelectChangeAssets}
                    placeholder={t("Select") + "..."}
                    isDisabled={report?.deleted_by_name || report?.is_deleted_content || report?.is_read_only_analyst}
                />
            ) : (
                <>
                    <p
                        className={`dt-field dt-skeleton ${isAnalystOrAnalystAdmin ? "dt-select-list" : ""
                            }`}
                    ></p>
                    <p className="dt-field dt-skeleton dt-update-date"></p>
                </>
            )}
        </div>
    );
};

const mapStatetoProps = (state) => {
    const { token, currentUser } = state.Login;
    return {
        currentUser,
        token,
        Organization: state.Organization
    };
};

export default withNamespaces()(connect(mapStatetoProps)(AffectedLegals));
